/**
 * Land in the sizes Uganda speaks (Nakasero Estates, Oct 2026: "we also
 * measure land in decimals — how can we have both m² and decimals?").
 *
 * A decimal is a hundredth of an acre: 40.47 m². A size is KEPT in m²
 * with the unit it was given in, and shown in that unit with m² beside it
 * ("30 decimals (1,214 m²)"), so the owner reads what they typed and a
 * buyer who thinks in metres reads metres.
 */

export const M2_PER_ACRE = 4046.8564224;
export const M2_PER_DECIMAL = M2_PER_ACRE / 100;

export type LandUnit = "m2" | "decimal" | "acre";

export const LAND_UNITS: readonly { value: LandUnit; label: string }[] = [
  { value: "m2", label: "m²" },
  { value: "decimal", label: "Decimals" },
  { value: "acre", label: "Acres" },
];

export function isLandUnit(u: unknown): u is LandUnit {
  return u === "m2" || u === "decimal" || u === "acre";
}

function round(n: number, d: number): number {
  const f = 10 ** d;
  return Math.round(n * f) / f;
}

/** A size given in `unit`, in m² (to two decimal places, so 12.5 decimals comes back as 12.5). */
export function toM2(value: number, unit: LandUnit): number {
  const v = Number(value) || 0;
  const m2 = unit === "decimal" ? v * M2_PER_DECIMAL : unit === "acre" ? v * M2_PER_ACRE : v;
  return round(m2, 2);
}

/** A size in m², in `unit`: whole m², decimals to 2 places, acres to 3. */
export function fromM2(m2: number, unit: LandUnit): number {
  const v = Number(m2) || 0;
  if (unit === "decimal") return round(v / M2_PER_DECIMAL, 2);
  if (unit === "acre") return round(v / M2_PER_ACRE, 3);
  return Math.round(v);
}

function num(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

/** "1 decimal", "30 decimals", "1 acre", "2.5 acres", "1,214 m²". */
export function landIn(m2: number, unit: LandUnit): string {
  const n = fromM2(m2, unit);
  if (unit === "decimal") return `${num(n)} decimal${n === 1 ? "" : "s"}`;
  if (unit === "acre") return `${num(n)} acre${n === 1 ? "" : "s"}`;
  return `${num(n)} m²`;
}

/**
 * A size in the unit it was given in, with the other measure beside it:
 * "30 decimals (1,214 m²)", "1,214 m² (30 decimals)", "2.5 acres (10,117 m²)".
 * Empty when there is no size.
 */
export function formatLand(m2: number | null | undefined, unit?: string | null): string {
  const v = Number(m2) || 0;
  if (!(v > 0)) return "";
  const u: LandUnit = isLandUnit(unit) ? unit : "m2";
  if (u === "m2") return `${landIn(v, "m2")} (${landIn(v, "decimal")})`;
  return `${landIn(v, u)} (${landIn(v, "m2")})`;
}
